import { useState } from 'react'
import { Button } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { ScrollArea } from '@creanizer/ui'
import { Search, Trash2, Eye, Edit3, Download, FileText, Folder } from 'lucide-react'
import type { SavedPlot } from '@creanizer/types'
import { ExportImportButtons } from '../../../../components/common'
import { usePlotStore } from '../../../../stores/plotStore'
import { useFolderStore } from '../../../../stores/folderStore'
import { PlotEditor } from './PlotEditor'
import { FolderManager, FolderMoveSelect } from './FolderManager'

interface SavedPlotsListProps {
  onLoad?: (plot: SavedPlot) => void
  onEdit?: (plotId: string) => void
}

export function SavedPlotsList({ onLoad, onEdit }: SavedPlotsListProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [editingPlot, setEditingPlot] = useState<SavedPlot | null>(null)
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null)
  const [showFolders, setShowFolders] = useState(true)
  const [draggedItemId, setDraggedItemId] = useState<string | null>(null)
  
  const { 
    savedPlots, 
    deletePlot, 
    clearAllPlots, 
    exportAsText, 
    exportAsJSON, 
    exportPlot,
    importFromJSON,
    updatePlot
  } = usePlotStore()
  
  const { getItemsInFolder, getItemFolder, moveItemBetweenFolders } = useFolderStore()
  
  // フォルダによるフィルタリング
  let folderFilteredPlots = savedPlots
  if (selectedFolderId === 'all') {
    folderFilteredPlots = savedPlots
  } else if (selectedFolderId === null) {
    // 未整理: どのフォルダにも属していないアイテム
    const allFolderItemIds = new Set<string>()
    const folders = useFolderStore.getState().getFolders('plot')
    folders.forEach(folder => {
      getItemsInFolder('plot', folder.id).forEach(id => allFolderItemIds.add(id))
    })
    folderFilteredPlots = savedPlots.filter(plot => !allFolderItemIds.has(plot.id))
  } else {
    const itemIds = getItemsInFolder('plot', selectedFolderId)
    folderFilteredPlots = savedPlots.filter(plot => itemIds.includes(plot.id))
  }
  
  // 検索によるフィルタリング
  const filteredPlots = folderFilteredPlots.filter(plot =>
    plot.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    plot.result.premise.toLowerCase().includes(searchTerm.toLowerCase()) ||
    plot.result.keywords.some(keyword => keyword.toLowerCase().includes(searchTerm.toLowerCase()))
  )

  const handleExportAsJSON = () => {
    exportAsJSON()
  }

  const handleExportSingleAsText = (plotId: string) => {
    const plot = savedPlots.find(p => p.id === plotId)
    if (!plot) return

    const text = `${plot.title}
    
【前提】
${plot.result.premise}

【あらすじ】
${plot.result.synopsis}

【キーワード】
${plot.result.keywords.join('、')}

【テーマ】
${plot.result.themes.join('、')}

【章構成】
${plot.result.structure.map((ch) => `第${ch.number}章：${ch.title}
${ch.summary}`).join('\n\n')}

【主要登場人物】
${plot.result.characters.map(char => `${char.name}: ${char.role}${char.description ? `\n  ${char.description}` : ''}`).join('\n')}
`

    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${plot.title}_${new Date().toISOString().split('T')[0]}.txt`
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleExportSingleAsJSON = (plotId: string) => {
    const plot = savedPlots.find(p => p.id === plotId)
    if (!plot) return

    const data = {
      version: '1.0',
      exportDate: new Date().toISOString(),
      plot
    }

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${plot.title}_${new Date().toISOString().split('T')[0]}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="card max-w-full overflow-x-hidden">
      <div className="flex items-center gap-2 mb-2">
        <FileText className="h-5 w-5" />
        <h2 className="text-xl font-semibold">
          保存済みプロット ({savedPlots.length}/500)
        </h2>
      </div>
      <p className="text-muted-foreground mb-6">
        生成したプロット設定を保存・管理
      </p>
      
      <div className="space-y-4 max-w-full">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="プロットを検索..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFolders(!showFolders)}
            className="px-2"
          >
            <Folder className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex gap-2">
          <ExportImportButtons
            onExportText={exportAsText}
            onExportJSON={handleExportAsJSON}
            onImportJSON={importFromJSON}
            dataTypeName="プロット"
          />

          {savedPlots.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                if (confirm('すべてのプロットを削除しますか？')) {
                  clearAllPlots()
                }
              }}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              全削除
            </Button>
          )}
        </div>

        <div className="flex gap-4">
          {/* フォルダサイドバー */}
          {showFolders && (
            <div className="w-48 border-r pr-4">
              <FolderManager
                generatorType="plot"
                items={savedPlots}
                selectedFolderId={selectedFolderId}
                onFolderSelect={setSelectedFolderId}
                draggedItemId={draggedItemId}
                onItemMove={(itemId, toFolderId) => {
                  const currentFolder = getItemFolder('plot', itemId)
                  moveItemBetweenFolders('plot', itemId, currentFolder?.id || null, toFolderId)
                }}
              />
            </div>
          )}
          
          {/* プロットリスト */}
          <div className="flex-1">
            <ScrollArea className="h-[500px] w-full overflow-x-hidden">
              <div className="space-y-2 max-w-full w-full">
                {filteredPlots.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    {searchTerm ? '検索結果がありません' : '保存済みのプロットがありません'}
                  </p>
                ) : (
                  filteredPlots.map((plot) => (
                    <div 
                      key={plot.id} 
                      className="card p-2 sm:p-4 overflow-hidden max-w-full w-[calc(100%-2rem)] sm:w-full mr-auto cursor-move hover:shadow-lg transition-shadow"
                      draggable
                      onDragStart={(e) => {
                        setDraggedItemId(plot.id)
                        e.dataTransfer.effectAllowed = 'move'
                      }}
                      onDragEnd={() => {
                        setDraggedItemId(null)
                      }}
                      style={{
                        opacity: draggedItemId === plot.id ? 0.5 : 1
                      }}
                    >
                  {/* モバイル版: 縦並びレイアウト */}
                  <div className="flex flex-col gap-3 sm:hidden w-full">
                    <div className="w-full">
                      <h3 className="font-medium text-sm truncate">{plot.title}</h3>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1 flex-wrap">
                        <span className="truncate">{plot.input.genreCustom || plot.input.genre}</span>
                        <span>•</span>
                        <span className="truncate">{plot.result.keywords.slice(0, 3).join('、')}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-2 truncate">
                        {plot.result.premise}
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">
                        {new Date(plot.createdAt).toLocaleDateString('ja-JP', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </p>
                      
                      {/* フォルダ選択 */}
                      <div className="mt-2">
                        <FolderMoveSelect
                          generatorType="plot"
                          itemId={plot.id}
                          currentFolderId={getItemFolder('plot', plot.id)?.id}
                          onMove={() => {}}
                        />
                      </div>
                    </div>
                    
                    {/* モバイル版: 横一列のボタン */}
                    <div className="flex gap-0.5 justify-start w-full">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onLoad?.(plot)}
                        title="読込"
                        className="w-12 h-7 px-1"
                      >
                        <Eye className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onEdit?.(plot.id)}
                        title="編集"
                        className="w-12 h-7 px-1"
                      >
                        <Edit3 className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleExportSingleAsText(plot.id)}
                        title="テキスト形式でエクスポート"
                        className="w-14 h-7 px-0.5 text-[9px]"
                      >
                        <Download className="h-2.5 w-2.5" />
                        <span className="ml-0.5">txt</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleExportSingleAsJSON(plot.id)}
                        title="JSON形式でエクスポート"
                        className="w-14 h-7 px-0.5 text-[9px]"
                      >
                        <Download className="h-2.5 w-2.5" />
                        <span className="ml-0.5">json</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (confirm('このプロットを削除しますか？')) {
                            deletePlot(plot.id)
                          }
                        }}
                        className="w-12 h-7 px-1"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* PC版: 既存の横並びレイアウト */}
                  <div className="hidden sm:flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <h3 className="font-semibold text-sm truncate">{plot.title}</h3>
                      
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1 flex-wrap">
                        <span className="truncate">{plot.input.genreCustom || plot.input.genre}</span>
                        <span>•</span>
                        <span className="truncate">{plot.result.keywords.slice(0, 3).join('、')}</span>
                        {plot.result.keywords.length > 3 && (
                          <>
                            <span>•</span>
                            <span>他{plot.result.keywords.length - 3}個</span>
                          </>
                        )}
                      </div>
                      
                      <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                        {plot.result.premise}
                      </p>
                      
                      <p className="text-xs text-muted-foreground mt-2">
                        {new Date(plot.createdAt).toLocaleDateString('ja-JP', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </p>
                      
                      {/* フォルダ選択（PC版） */}
                      <div className="mt-2 w-32">
                        <FolderMoveSelect
                          generatorType="plot"
                          itemId={plot.id}
                          currentFolderId={getItemFolder('plot', plot.id)?.id}
                          onMove={() => {}}
                        />
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <div className="flex flex-col gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onLoad?.(plot)}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          読込
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleExportSingleAsText(plot.id)}
                          title="テキスト形式でエクスポート"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          txt
                        </Button>
                      </div>
                      
                      <div className="flex flex-col gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onEdit?.(plot.id)}
                        >
                          <Edit3 className="h-4 w-4 mr-2" />
                          編集
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleExportSingleAsJSON(plot.id)}
                          title="JSON形式でエクスポート"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          json
                        </Button>
                      </div>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (confirm('このプロットを削除しますか？')) {
                            deletePlot(plot.id)
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
              </div>
            </ScrollArea>
          </div>
        </div>

        {savedPlots.length >= 450 && (
          <p className="text-xs text-muted-foreground text-center">
            保存可能件数の上限（500件）に近づいています
          </p>
        )}
      </div>
      
      {editingPlot && (
        <PlotEditor
          plot={editingPlot}
          onClose={() => {
            updatePlot(editingPlot.id, editingPlot.result)
            setEditingPlot(null)
          }}
        />
      )}
    </div>
  )
}